<template>
  <analytics-page-layout
    title="{organization-label}: Guest Check-In Analytics"
    heading="Guest Check-In Analytics"
    subheading="Insights into guest check-in submissions for your organization"
    :loading="loading"
    :is_empty="has_no_data"
    :back_route="{
      name: 'all-analytics',
      params: { organization_id: $rc.getOrganizationId() },
    }"
  >
    <template #filters>
      <v-row align="start" :dense="$vuetify.breakpoint.smAndUp">
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="selected_form_id"
            :items="form_options"
            label="Guest Check-In Form"
            prepend-inner-icon="mdi-form-select"
            outlined
            dense
            hide-details
            @change="loadAnalytics"
          />
        </v-col>

        <v-col cols="12" sm="5" md="7">
          <date-range-selector
            ref="dateRangeSelector"
            v-model="date_range"
            @change="handleDateRangeChange"
          />
        </v-col>

        <v-col cols="12" sm="3" md="2">
          <div class="d-flex justify-end align-center fill-height">
            <v-btn text color="primary" @click="clearFilters">
              <v-icon left>mdi-filter-off</v-icon>
              Clear
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon v-bind="attrs" v-on="on" @click="settings_modal = true">
                  <v-icon>mdi-cog</v-icon>
                </v-btn>
              </template>
              <span>Display Settings</span>
            </v-tooltip>
          </div>
        </v-col>
      </v-row>
    </template>

    <template #content>
      <div>
        <v-tabs v-model="current_tab" background-color="transparent" color="primary">
          <v-tab>
            <v-icon left>mdi-chart-bar</v-icon>
            Overview
          </v-tab>
          <v-tab>
            <v-icon left>mdi-check-decagram</v-icon>
            Consent
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="current_tab" class="tabs-content">
          <v-tab-item>
            <kpi-grid :kpis="overview_kpis" :show_zero_values="show_zero_values" />

            <section class="mb-8">
              <v-row align="start">
                <v-col cols="12" lg="8">
                  <chart-card
                    title="Daily Submissions Trend"
                    :has_data="has_submission_time_series_data"
                    no_data_icon="mdi-chart-line"
                  >
                    <m-chart-line
                      title=""
                      :labels="analytics.submission_time_series.chart_js_labels"
                      :data="analytics.submission_time_series.chart_js_data"
                      point_label="Submission"
                      :decimal_y_axis="false"
                    />
                  </chart-card>

                  <chart-card
                    class="mt-6"
                    title="Contact Method Breakdown"
                    :has_data="has_contact_method_breakdown_data"
                    no_data_icon="mdi-chart-donut"
                  >
                    <m-chart-donut
                      title=""
                      :labels="analytics.contact_method_breakdown.chart_js_labels"
                      :data="analytics.contact_method_breakdown.chart_js_data"
                      :table_headers="analytics.contact_method_breakdown.headers"
                      :table_rows="analytics.contact_method_breakdown.rows"
                      :colors="categorical_colors"
                    />
                  </chart-card>
                </v-col>

                <v-col cols="12" lg="4">
                  <chart-card
                    title="Submissions by Form"
                    :has_data="has_form_breakdown_data"
                    no_data_icon="mdi-chart-donut"
                  >
                    <m-chart-donut
                      title=""
                      :labels="analytics.form_breakdown.chart_js_labels"
                      :data="analytics.form_breakdown.chart_js_data"
                      :table_headers="analytics.form_breakdown.headers"
                      :table_rows="analytics.form_breakdown.rows"
                      :colors="categorical_colors"
                    />
                  </chart-card>
                </v-col>
              </v-row>
            </section>
          </v-tab-item>

          <v-tab-item>
            <kpi-grid :kpis="engagement_kpis" :show_zero_values="show_zero_values" />

            <v-row>
              <v-col cols="12" lg="6">
                <chart-card
                  title="Form Opt-In Rate - Email"
                  :has_data="has_email_opt_in_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.email_opt_in_breakdown.chart_js_labels"
                    :data="analytics.email_opt_in_breakdown.chart_js_data"
                    :table_headers="analytics.email_opt_in_breakdown.headers"
                    :table_rows="analytics.email_opt_in_breakdown.rows"
                    :colors="binary_colors"
                  />
                </chart-card>
              </v-col>

              <v-col cols="12" lg="6">
                <chart-card
                  title="Form Opt-In Rate - Phone"
                  :has_data="has_phone_opt_in_breakdown_data"
                  no_data_icon="mdi-chart-donut"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.phone_opt_in_breakdown.chart_js_labels"
                    :data="analytics.phone_opt_in_breakdown.chart_js_data"
                    :table_headers="analytics.phone_opt_in_breakdown.headers"
                    :table_rows="analytics.phone_opt_in_breakdown.rows"
                    :colors="binary_colors"
                  />
                </chart-card>
              </v-col>
            </v-row>

            <v-row align="start">
              <v-col
                v-if="has_double_opt_in_time_series_data || has_doi_completion_breakdown_data"
                cols="12"
                :lg="has_double_opt_in_breakdown_data ? 8 : 12"
              >
                <chart-card
                  v-if="has_double_opt_in_time_series_data"
                  title="Double Opt-In Trend"
                  class="mb-6"
                >
                  <m-chart-line
                    title=""
                    :labels="analytics.double_opt_in_time_series.chart_js_labels"
                    :data="analytics.double_opt_in_time_series.chart_js_data"
                    point_label="Double Opt-In"
                    :decimal_y_axis="false"
                  />
                </chart-card>

                <chart-card
                  v-if="has_doi_completion_breakdown_data"
                  title="Double Opt-In Completion Speed"
                >
                  <m-chart-donut
                    title=""
                    :labels="analytics.doi_completion_breakdown.chart_js_labels"
                    :data="analytics.doi_completion_breakdown.chart_js_data"
                    :table_headers="analytics.doi_completion_breakdown.headers"
                    :table_rows="analytics.doi_completion_breakdown.rows"
                    :colors="timing_colors"
                  />
                </chart-card>
              </v-col>

              <v-col v-if="has_double_opt_in_breakdown_data" cols="12" lg="4">
                <chart-card title="Double Opt-In Breakdown" no_data_icon="mdi-chart-donut">
                  <m-chart-donut
                    title=""
                    :labels="analytics.double_opt_in_breakdown.chart_js_labels"
                    :data="analytics.double_opt_in_breakdown.chart_js_data"
                    :table_headers="analytics.double_opt_in_breakdown.headers"
                    :table_rows="analytics.double_opt_in_breakdown.rows"
                    :colors="timing_colors"
                  />
                </chart-card>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </div>
    </template>

    <template #dialogs>
      <analytics-settings-dialog
        v-model="settings_modal"
        :show_zero_values.sync="show_zero_values"
      />
    </template>
  </analytics-page-layout>
</template>

<script>
import _ from 'lodash';
import { prettyPrintDurationv2 } from '@/js/time.js';
import { CHART_COLOR_SETS, getCategoricalColors, getTimingColors } from '@/js/analytics-colors.js';
import AnalyticsPageLayout from '@/components/analytics/analytics-page-layout.vue';
import DateRangeSelector from '@/components/analytics/date-range-selector.vue';
import ChartCard from '@/components/analytics/chart-card.vue';
import KpiGrid from '@/components/analytics/kpi-grid.vue';
import AnalyticsSettingsDialog from '@/components/analytics/analytics-settings-dialog.vue';

export default {
  name: 'GuestCheckInAnalytics',

  components: {
    AnalyticsPageLayout,
    DateRangeSelector,
    ChartCard,
    KpiGrid,
    AnalyticsSettingsDialog,
  },

  data() {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    return {
      current_tab: null,
      analytics: {},
      forms: [],
      selected_form_id: 'all',
      date_range: {
        start: this.formatDateForPicker(thirtyDaysAgo),
        end: this.formatDateForPicker(today),
      },
      loading: true,
      exporting: false,
      show_zero_values: false,
      settings_modal: false,
    };
  },

  computed: {
    has_no_data() {
      return !this.loading && this.analytics && this.analytics.total_submissions === 0;
    },

    overview_kpis() {
      return [
        {
          label: 'Total Check-Ins',
          value: (this.analytics.total_submissions || 0).toLocaleString(),
          tooltip:
            'The total number of times guests have checked in using this form during the selected date range.',
        },
        {
          label: 'Check-Ins with Phone',
          value: (this.analytics.submissions_with_phone || 0).toLocaleString(),
          tooltip: "The total number of check-ins that included a guest's phone number.",
        },
        {
          label: 'Check-Ins with Email',
          value: (this.analytics.submissions_with_email || 0).toLocaleString(),
          tooltip: "The total number of check-ins that included a guest's email address.",
        },
        {
          label: 'Check-Ins with At Least One Contact Method',
          value: `${this.usable_contact_rate_percent}%`,
          tooltip:
            'The percentage of check-ins that provided at least one method of contact (phone or email).',
        },
        {
          label: 'Check-Ins with Both Phone and Email',
          value: `${this.both_contact_rate_percent}%`,
          tooltip:
            'The percentage of check-ins where guests provided both a phone number and an email, creating a more complete profile.',
        },
        {
          label: 'New Contacts Created in CRM',
          value: (this.analytics.crm_customers_created || 0).toLocaleString(),
          tooltip:
            'The number of new contact profiles created in your CRM from check-ins. Requires a compatible CRM integration and the setting to create new customers on check-in.',
        },
        {
          label: 'Existing Contacts Updated in CRM',
          value: (this.analytics.crm_customers_updated || 0).toLocaleString(),
          tooltip:
            'The number of existing contact profiles in your CRM that were updated with new information from check-ins. Requires a CRM integration and the setting to update existing customers on check-in.',
        },
        {
          label: 'Double Opt-In Rate',
          value: `${this.completion_rate_percent}%`,
          tooltip:
            'The percentage of guests who successfully confirmed their double opt-in after checking in. This feature requires double opt-in to be enabled on your form.',
        },
        {
          label: 'Returning Guests',
          value: (this.analytics.repeat_check_ins || 0).toLocaleString(),
          tooltip:
            'The number of unique guests in this time period (identified by phone or email) who have checked in more than once.',
        },
      ];
    },

    engagement_kpis() {
      return [
        {
          label: 'Form Email Opt-In Rate',
          value: `${this.email_consent_rate_percent}%`,
          tooltip:
            'The percentage of guests who provided an email and agreed to receive email communications on a guest check-in form.',
        },
        {
          label: 'Form Phone Opt-In Rate',
          value: `${this.phone_consent_rate_percent}%`,
          tooltip:
            'The percentage of guests who provided a phone number and agreed to receive text message communications on a guest check-in form.',
        },
        {
          label: 'Double Opt-In Rate',
          value: `${this.completion_rate_percent}%`,
          tooltip:
            'The percentage of guests who successfully confirmed their subscription after checking in. This feature requires double opt-in to be enabled on your form.',
        },
        {
          label: 'Typical Double Opt-In Confirmation Time',
          value: `${this.median_doi_minutes}`,
          tooltip:
            'The median time guests take to confirm their opt-in after checking in. This is the time it took for 50% of your guests to confirm their double opt-in.',
        },
        {
          label: 'P90 Double Opt-In Confirmation Time',
          value: `${this.p90_doi_minutes}`,
          tooltip:
            'The time it took for 90% of your guests to confirm their double opt-in. This shows the upper range of your customer response times.',
        },
      ];
    },

    completion_rate_percent() {
      if (!this.analytics.completion_rate) return 0;
      return Math.round(this.analytics.completion_rate * 100);
    },

    email_consent_rate_percent() {
      const rate = this.analytics.email_opt_in_rate;
      if (rate === undefined || rate === null) return 0;
      return Math.round((rate || 0) * 100);
    },

    phone_consent_rate_percent() {
      const rate = this.analytics.sms_opt_in_rate;
      if (rate === undefined || rate === null) return 0;
      return Math.round((rate || 0) * 100);
    },

    usable_contact_rate_percent() {
      const rate = this.analytics.usable_contact_rate;
      if (rate === undefined || rate === null) return 0;
      return Math.round((rate || 0) * 100);
    },

    both_contact_rate_percent() {
      const rate = this.analytics.both_contact_rate;
      if (rate === undefined || rate === null) return 0;
      return Math.round((rate || 0) * 100);
    },

    median_doi_minutes() {
      const ms = this.analytics.doi_completion_median_ms || 0;
      if (ms === undefined || ms === null) return 0;

      return prettyPrintDurationv2(ms);
    },

    p90_doi_minutes() {
      const ms = this.analytics.doi_completion_p90_ms || 0;
      return prettyPrintDurationv2(ms);
    },
    form_options() {
      const all_forms_option = { text: 'All Forms', value: 'all' };
      if (!this.forms) return [all_forms_option];
      return [
        all_forms_option,
        ...this.forms.map(form => ({
          text: form.internal_label || 'Untitled Form',
          value: form.guest_check_in_form_id,
        })),
      ];
    },

    time_start() {
      if (!this.date_range.start) return null;
      return new Date(`${this.date_range.start}T00:00:00Z`).getTime();
    },

    time_stop() {
      if (!this.date_range.end) return null;
      return new Date(`${this.date_range.end}T23:59:59Z`).getTime();
    },

    has_submission_time_series_data() {
      return (
        this.analytics.submission_time_series &&
        this.analytics.submission_time_series.chart_js_data &&
        this.analytics.submission_time_series.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_form_breakdown_data() {
      return (
        this.analytics.form_breakdown &&
        this.analytics.form_breakdown.chart_js_data &&
        this.analytics.form_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_double_opt_in_time_series_data() {
      return (
        this.analytics.double_opt_in_time_series &&
        this.analytics.double_opt_in_time_series.chart_js_data &&
        this.analytics.double_opt_in_time_series.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_double_opt_in_breakdown_data() {
      return (
        this.analytics.double_opt_in_breakdown &&
        this.analytics.double_opt_in_breakdown.chart_js_data &&
        this.analytics.double_opt_in_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_email_opt_in_breakdown_data() {
      return (
        this.analytics.email_opt_in_breakdown &&
        this.analytics.email_opt_in_breakdown.chart_js_data &&
        this.analytics.email_opt_in_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_phone_opt_in_breakdown_data() {
      return (
        this.analytics.phone_opt_in_breakdown &&
        this.analytics.phone_opt_in_breakdown.chart_js_data &&
        this.analytics.phone_opt_in_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_doi_completion_breakdown_data() {
      return (
        this.analytics.doi_completion_breakdown &&
        this.analytics.doi_completion_breakdown.chart_js_data &&
        this.analytics.doi_completion_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    has_contact_method_breakdown_data() {
      return (
        this.analytics.contact_method_breakdown &&
        this.analytics.contact_method_breakdown.chart_js_data &&
        this.analytics.contact_method_breakdown.chart_js_data.reduce((a, b) => a + b, 0) > 0
      );
    },

    categorical_colors() {
      const formCount = this.analytics.form_breakdown?.chart_js_data?.length || 0;
      const contactCount = this.analytics.contact_method_breakdown?.chart_js_data?.length || 0;
      const maxCount = Math.max(formCount, contactCount, 6);
      return getCategoricalColors(maxCount);
    },

    binary_colors() {
      return CHART_COLOR_SETS.binary;
    },

    timing_colors() {
      const doiCount = this.analytics.double_opt_in_breakdown?.chart_js_data?.length || 0;
      const completionCount = this.analytics.doi_completion_breakdown?.chart_js_data?.length || 0;
      const maxCount = Math.max(doiCount, completionCount, 5);
      return getTimingColors(maxCount);
    },
  },

  watch: {
    show_zero_values(new_value) {
      localStorage.setItem('guestCheckInAnalytics.showZeroValues', new_value.toString());
    },
  },

  mounted() {
    this.loadShowZeroValuesSetting();
    this.initializeFromQueryParams();
    this.loadForms();
    this.loadAnalytics();
  },

  methods: {
    handleDateRangeChange() {
      this.loadAnalytics();
    },

    loadShowZeroValuesSetting() {
      const saved_setting = localStorage.getItem('guestCheckInAnalytics.showZeroValues');
      if (saved_setting !== null) {
        this.show_zero_values = saved_setting === 'true';
      }
    },

    formatOrderValue(milli_cents) {
      const dollars = milli_cents / 10000;
      return `$${dollars.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },

    clearFilters() {
      this.selected_form_id = 'all';
      if (this.$refs.dateRangeSelector) {
        this.$refs.dateRangeSelector.resetToDefault();
      }
      this.loadAnalytics();
    },

    initializeFromQueryParams() {
      if (this.$route.query.guest_check_in_form_id) {
        this.selected_form_id = this.$route.query.guest_check_in_form_id;
      }
    },

    loadForms() {
      this.$rc.rest({
        spinner: false,
        endpoint: 'POST: /api/v1/guest-check-in/forms/search',
        params: { organization_id: this.$route.params.organization_id, is_published: true },
        on_success: data => {
          this.forms = data.results || [];
        },
        on_error: 'Unable to load guest check-in forms',
      });
    },

    loadAnalytics() {
      this.loading = true;

      const params = {
        organization_id: this.$route.params.organization_id,
        time_start: this.time_start,
        time_stop: this.time_stop,
      };
      if (this.selected_form_id) params.guest_check_in_form_id = this.selected_form_id;

      const minDelay = new Promise(resolve => setTimeout(resolve, 300));

      Promise.all([
        minDelay,
        this.$rc.rest({
          spinner: false,
          endpoint: 'PUT: /api/v1/guest-check-in/analytics/summary',
          params,
          on_success: data => {
            this.analytics = data;
          },
          on_error: () => {
            this.$rc.showError('Unable to load analytics data');
          },
        }),
      ]).finally(() => {
        this.loading = false;
      });
    },

    exportSubmissions() {
      this.exporting = true;

      const params = {
        organization_id: this.$route.params.organization_id,
        time_start: this.time_start,
        time_stop: this.time_stop,
      };
      if (this.selected_form_id) params.guest_check_in_form_id = this.selected_form_id;
      this.$rc.excelExport('EXPORT: /api/v1/guest-check-in/submissions', params, () => {});
      this.exporting = false;
    },

    formatDateForPicker(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style>
.v-main {
  padding-top: 42px !important;
}
</style>

<style lang="scss" scoped>
.tabs-content {
  background-color: transparent !important;
}

:deep(.v-tooltip__content) {
  background: rgba(33, 33, 33, 0.97);
  color: white;
  font-size: 12px;
  padding: 8px 12px;
  border-radius: 4px;
  max-width: 250px;
  text-align: left;
}
</style>
